import {
  Banner,
  Button,
  DataTable,
  ErrorState,
  FilterToolbar,
  Modal,
  notify,
  PageHeader,
  SearchInput,
  Section,
  SegmentedControl,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  TextAreaField,
  TextField,
  useClientRows,
  useDebouncedText,
  type Column,
  type SortValue,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";

type Block = { type: "heading" | "text" | "button" | "divider"; text?: string; url?: string };
export type Campaign = {
  id: string;
  name: string;
  subject: string;
  preheader: string | null;
  blocks: Block[];
  segmentId: string | null;
  status: "draft" | "pending_approval" | "approved" | "scheduled";
  createdBy: string | null;
  submittedBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  updatedAt: string;
};
type Segment = { id: string; name: string; rules: { tags?: string[]; customersOnly?: boolean }; total: number; reachable: number };
type Contact = { id: string; email: string | null; phone: string | null; name: string | null; company: string | null; tags: string[]; consent: { email: string | null; sms: string | null }; reachable: { email: boolean }; suppressed: boolean; createdAt: string };
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const BLOCK_LABEL: Record<Block["type"], string> = { heading: "Heading", text: "Text", button: "Button", divider: "Divider" };

/** Shown on every marketing screen: nothing can go out tonight. */
function SendingOff() {
  return (
    <Banner tone="info" title={<StatusBadge domain="integration" value="Sending off" size="sm" />}>
      Nothing is sent from marketing yet: no sending service is connected, and sending and autopilot stay off until Leo approves outbound marketing.
    </Banner>
  );
}

const CAMPAIGN_SORT: Record<string, SortValue<Campaign>> = { name: (c) => c.name, author: (c) => c.createdBy, updated: (c) => c.updatedAt };

/** Marketing > Campaigns. */
export function CampaignsPage() {
  const me = useConsoleMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["console", "mkt", "campaigns"], queryFn: () => api<{ campaigns: Campaign[] }>("/v1/console/marketing/campaigns") });
  const create = useMutation({
    mutationFn: () => api<Campaign>("/v1/console/marketing/campaigns", { method: "POST", body: { name: `Untitled campaign ${new Date().toLocaleDateString("en-US")}`, subject: "", blocks: [] } }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["console", "mkt", "campaigns"] });
      navigate(`/marketing/campaigns/${c.id}`);
    },
    onError: (e) => notify.error("Couldn't create the campaign", message(e)),
  });
  const table = useClientRows(list.data?.campaigns ?? [], { values: CAMPAIGN_SORT, initial: { key: "updated", dir: "desc" } });
  const columns: Column<Campaign>[] = [
    { key: "name", header: "Campaign", primary: true, sortable: true, truncate: true, text: (c) => c.name, render: (c) => <Link className="ffa-rowlink" to={`/marketing/campaigns/${c.id}`}>{c.name}</Link>, sub: (c) => c.subject || "No subject yet" },
    { key: "status", header: "Status", width: "12rem", render: (c) => <StatusBadge domain="campaign" value={c.status} size="sm" /> },
    { key: "author", header: "Author", sortable: true, width: "10rem", mobile: "hide", render: (c) => c.createdBy ?? "-" },
    { key: "updated", header: "Updated", sortable: true, sortFirst: "desc", width: "10rem", render: (c) => <time dateTime={c.updatedAt}>{when.format(new Date(c.updatedAt))}</time> },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Campaigns"
        subtitle="Email campaigns to people who opted in. Every campaign needs a second person to approve it."
        primaryAction={me.capabilities.includes("marketing:write") ? <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>New campaign</Button> : undefined}
      />
      <SendingOff />
      {me.capabilities.includes("marketing:approve") ? <SenderSettings /> : null}
      <DataTable
        caption="Campaigns"
        columns={columns}
        {...table}
        rowKey={(c) => c.id}
        loading={list.isPending}
        error={list.isError ? { title: "Couldn't load campaigns", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty="No campaigns yet."
        onRowClick={(c) => navigate(`/marketing/campaigns/${c.id}`)}
        rowHref={(c) => `/marketing/campaigns/${c.id}`}
      />
    </section>
  );
}

const backToCampaigns = (
  <Link to="/marketing/campaigns" className="ffa-back">
    ← Campaigns
  </Link>
);

/** Marketing > Campaigns > one campaign: blocks on the left, the email as recipients would see it on the right. */
export function CampaignEditor() {
  const { campaignId = "" } = useParams();
  const me = useConsoleMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["console", "mkt", "campaign", campaignId], queryFn: () => api<{ campaign: Campaign; footer: { senderName: string | null; postalAddress: string | null } }>(`/v1/console/marketing/campaigns/${campaignId}`) });
  const segs = useQuery({ queryKey: ["console", "mkt", "segments"], queryFn: () => api<{ segments: Segment[] }>("/v1/console/marketing/segments") });
  const [f, setF] = useState<{ name: string; subject: string; preheader: string; blocks: Block[]; segmentId: string }>({ name: "", subject: "", preheader: "", blocks: [], segmentId: "" });
  const [device, setDevice] = useState<"desktop" | "phone">("desktop");
  useEffect(() => {
    const c = q.data?.campaign;
    if (c) setF({ name: c.name, subject: c.subject, preheader: c.preheader ?? "", blocks: c.blocks, segmentId: c.segmentId ?? "" });
  }, [q.data]);
  const refresh = (c: Campaign) => {
    qc.setQueryData(["console", "mkt", "campaign", campaignId], (old: typeof q.data) => (old ? { ...old, campaign: c } : old));
    void qc.invalidateQueries({ queryKey: ["console", "mkt", "campaigns"] });
  };
  const save = useMutation({
    mutationFn: () => api<Campaign>(`/v1/console/marketing/campaigns/${campaignId}`, { method: "PATCH", body: { name: f.name.trim(), subject: f.subject.trim(), preheader: f.preheader.trim() || null, blocks: f.blocks, segmentId: f.segmentId || null } }),
    onSuccess: (c) => {
      refresh(c);
      notify.success("Saved", q.data?.campaign.status === "approved" ? "Editing sent it back to draft; it needs approval again." : undefined);
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const act = useMutation({
    mutationFn: (action: "submit" | "approve" | "reject" | "schedule") => api<{ campaign: Campaign }>(`/v1/console/marketing/campaigns/${campaignId}/${action}`, { method: "POST", body: {} }),
    onSuccess: (r, action) => {
      refresh(r.campaign);
      notify.success(action === "submit" ? "Sent for approval" : action === "approve" ? "Approved" : "Returned to draft");
    },
    onError: (e) => notify.error("Not done", message(e)),
  });
  const canWrite = me.capabilities.includes("marketing:write");
  const canApprove = me.capabilities.includes("marketing:approve");
  const c = q.data?.campaign;
  const footer = q.data?.footer;
  const setBlock = (i: number, patch: Partial<Block>) => setF((x) => ({ ...x, blocks: x.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const move = (i: number, d: -1 | 1) =>
    setF((x) => {
      const b = [...x.blocks];
      const [it] = b.splice(i, 1);
      b.splice(i + d, 0, it!);
      return { ...x, blocks: b };
    });
  if (!c) {
    return (
      <section className="ffa-page" aria-busy={!q.isError || undefined}>
        <PageHeader back={backToCampaigns} eyebrow="Campaign" title={q.isError ? "Campaign" : <Skeleton width="16rem" height="2.25rem" />} />
        {q.isError ? (
          <ErrorState title="Couldn't load this campaign" message={message(q.error)} onRetry={() => void q.refetch()} />
        ) : (
          <div className="ffa-editor">
            <Section><SkeletonText lines={6} /></Section>
            <Section><SkeletonText lines={6} /></Section>
          </div>
        )}
      </section>
    );
  }
  const seg = segs.data?.segments.find((s) => s.id === f.segmentId);
  const mine = c.submittedBy === me.user.name || c.createdBy === me.user.name;
  const approving = canApprove && c.status === "pending_approval";
  // The next step in the approval flow is the primary action; Save sits beside it.
  const next = approving ? (
    <Button variant="primary" disabled={mine} title={mine ? "Someone other than the author must approve" : undefined} onClick={() => act.mutate("approve")}>Approve</Button>
  ) : canWrite && c.status === "draft" ? (
    <Button variant="primary" onClick={() => act.mutate("submit")}>Submit for approval</Button>
  ) : canApprove && c.status === "approved" ? (
    <Button disabled title="No sending service is connected">Schedule (sending off)</Button>
  ) : null;
  return (
    <section className="ffa-page">
      <PageHeader
        back={backToCampaigns}
        eyebrow="Campaign"
        title={c.name}
        badges={<StatusBadge domain="campaign" value={c.status} />}
        subtitle={c.approvedBy ? `Approved by ${c.approvedBy}` : c.submittedBy ? `Submitted by ${c.submittedBy}` : "Draft: a second person approves it before it can be scheduled."}
        secondaryActions={
          <>
            {canWrite && next ? <Button loading={save.isPending} onClick={() => save.mutate()}>Save</Button> : null}
            {approving ? <Button onClick={() => act.mutate("reject")}>Return to draft</Button> : null}
          </>
        }
        primaryAction={next ?? (canWrite ? <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button> : undefined)}
      />
      <SendingOff />
      <div className="ffa-editor">
        <div className="ffa-editor__form">
          <Section fieldset disabled={!canWrite} title="Details">
            <div className="ffx-fields">
              <TextField className="ffx-span" label="Campaign name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} />
              <TextField className="ffx-span" label="Subject line" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={150} />
              <TextField className="ffx-span" label="Preview text" optional value={f.preheader} onChange={(e) => setF({ ...f, preheader: e.target.value })} maxLength={150} />
              <Select
                className="ffx-span"
                label="Audience"
                value={f.segmentId}
                onChange={(v) => setF({ ...f, segmentId: v })}
                options={[{ value: "", label: "Choose a segment" }, ...(segs.data?.segments ?? []).map((s) => ({ value: s.id, label: s.name, meta: `${s.reachable} reachable` }))]}
                hint={seg ? `${seg.reachable} of ${seg.total} contacts have opted in and aren't suppressed.` : undefined}
              />
            </div>
          </Section>
          <Section
            fieldset
            disabled={!canWrite}
            title="Content"
            actions={
              canWrite ? (
                <span className="ffa-btnrow">
                  {(["heading", "text", "button", "divider"] as const).map((t) => (
                    <Button key={t} size="sm" onClick={() => setF((x) => ({ ...x, blocks: [...x.blocks, t === "divider" ? { type: t } : t === "button" ? { type: t, text: "Get a quote", url: "https://app.flexfreight.net/quote" } : { type: t, text: "" }] }))}>
                      Add {t}
                    </Button>
                  ))}
                </span>
              ) : null
            }
          >
            {f.blocks.length === 0 ? <p className="ffa-muted ffa-small">No content yet. Add a heading, text or a button.</p> : null}
            {f.blocks.map((b, i) => (
              <div key={i} className="ffa-block-edit">
                <div className="ffa-block-edit__head">
                  <span className="ffx-micro">{BLOCK_LABEL[b.type]}</span>
                  {canWrite ? (
                    <span className="ffa-btnrow">
                      <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${b.type} up`}>Up</Button>
                      <Button size="sm" variant="ghost" disabled={i === f.blocks.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${b.type} down`}>Down</Button>
                      <Button size="sm" variant="ghost" onClick={() => setF((x) => ({ ...x, blocks: x.blocks.filter((_, j) => j !== i) }))} aria-label={`Remove ${b.type}`}>Remove</Button>
                    </span>
                  ) : null}
                </div>
                {b.type === "heading" ? <TextField size="sm" label="Heading" value={b.text ?? ""} onChange={(e) => setBlock(i, { text: e.target.value })} /> : null}
                {b.type === "text" ? <TextAreaField label="Text" value={b.text ?? ""} onChange={(e) => setBlock(i, { text: e.target.value })} rows={3} /> : null}
                {b.type === "button" ? (
                  <div className="ffx-fields">
                    <TextField size="sm" label="Button label" value={b.text ?? ""} onChange={(e) => setBlock(i, { text: e.target.value })} />
                    <TextField size="sm" label="Link (https)" value={b.url ?? ""} onChange={(e) => setBlock(i, { url: e.target.value })} />
                  </div>
                ) : null}
              </div>
            ))}
          </Section>
        </div>
        <Section title="Email preview" actions={<SegmentedControl label="Preview size" options={[{ value: "desktop", label: "Desktop" }, { value: "phone", label: "Phone" }]} value={device} onChange={setDevice} />} className="ffa-preview">
          <div className={`ffa-email ffa-email--${device}`}>
            <div className="ffa-email__meta">
              <strong>{f.subject || "No subject"}</strong>
              <span className="ffa-muted ffa-small">{footer?.senderName ?? "FlexFreight"} · {f.preheader || "No preview text"}</span>
            </div>
            <div className="ffa-email__body">
              {f.blocks.length === 0 ? <p className="ffa-muted">Add content blocks to see the email.</p> : null}
              {f.blocks.map((b, i) =>
                b.type === "heading" ? <h3 key={i}>{b.text}</h3> : b.type === "text" ? <p key={i} className="ffa-msg__body">{b.text}</p> : b.type === "button" ? <span key={i} className="ffa-email__button">{b.text}</span> : <hr key={i} />,
              )}
              <footer className="ffa-email__footer">
                You get this because you opted in to FlexFreight updates. <u>Unsubscribe</u>.
                <br />
                {footer?.postalAddress ?? <span className="ffa-warn">Mailing address missing (required): set it in marketing settings.</span>}
              </footer>
            </div>
          </div>
        </Section>
      </div>
    </section>
  );
}

const CONTACT_SORT: Record<string, SortValue<Contact>> = { contact: (c) => c.name ?? c.email ?? c.phone, consent: (c) => c.consent.email };
const reach = (c: Contact) => (c.suppressed ? "suppressed" : c.reachable.email ? "reachable" : "unreachable");
const SEGMENT_SORT: Record<string, SortValue<Segment>> = { name: (s) => s.name, reachable: (s) => s.reachable, total: (s) => s.total };

/** Marketing > Audiences: contacts with their consent, suppressions, and segments. */
export function AudiencesPage() {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [search, setSearch] = useDebouncedText(q, setQ);
  const contacts = useQuery({ queryKey: ["console", "mkt", "contacts", q], queryFn: () => api<{ contacts: Contact[]; counts: { total: number; reachable: number } }>(`/v1/console/marketing/contacts?${new URLSearchParams({ q })}`), placeholderData: keepPreviousData });
  const segs = useQuery({ queryKey: ["console", "mkt", "segments"], queryFn: () => api<{ segments: Segment[] }>("/v1/console/marketing/segments") });
  const [adding, setAdding] = useState(false);
  const [segName, setSegName] = useState("");
  const [segTags, setSegTags] = useState("");
  const addSeg = useMutation({
    mutationFn: () => api("/v1/console/marketing/segments", { method: "POST", body: { name: segName.trim(), rules: { tags: segTags.split(/[\s,]+/).filter(Boolean) } } }),
    onSuccess: () => {
      setSegName("");
      setSegTags("");
      void qc.invalidateQueries({ queryKey: ["console", "mkt", "segments"] });
      notify.success("Segment added");
    },
    onError: (e) => notify.error("Couldn't add the segment", message(e)),
  });
  const canWrite = me.capabilities.includes("marketing:write");
  // Server order until someone sorts by a column.
  const people = useClientRows(contacts.data?.contacts ?? [], { values: CONTACT_SORT });
  const segments = useClientRows(segs.data?.segments ?? [], { values: SEGMENT_SORT });
  const columns: Column<Contact>[] = [
    { key: "contact", header: "Contact", primary: true, sortable: true, truncate: true, text: (c) => c.name ?? c.email ?? c.phone ?? "", render: (c) => c.name ?? c.email ?? c.phone, sub: (c) => [c.email, c.company].filter(Boolean).join(" · ") },
    { key: "consent", header: "Email consent", sortable: true, width: "10rem", render: (c) => (c.consent.email === "opted_in" ? "Opted in" : c.consent.email === "opted_out" ? "Opted out" : <span className="ffa-muted">Not recorded</span>) },
    { key: "reachable", header: "Reachable", width: "9rem", render: (c) => <StatusBadge domain="contactReach" value={reach(c)} size="sm" /> },
    { key: "tags", header: "Tags", width: "12rem", mobile: "hide", truncate: true, text: (c) => c.tags.join(", "), render: (c) => c.tags.join(", ") || "-" },
  ];
  const segColumns: Column<Segment>[] = [
    { key: "name", header: "Segment", primary: true, sortable: true, truncate: true, text: (s) => s.name, render: (s) => s.name },
    { key: "tags", header: "Tags", truncate: true, text: (s) => s.rules.tags?.join(", ") || "Any", render: (s) => s.rules.tags?.join(", ") || <span className="ffa-muted">Any</span> },
    { key: "reachable", header: "Reachable", numeric: true, sortable: true, width: "8rem", render: (s) => s.reachable.toLocaleString("en-US") },
    { key: "total", header: "Contacts", numeric: true, sortable: true, width: "8rem", render: (s) => s.total.toLocaleString("en-US") },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Audiences"
        subtitle="Contacts, their email consent and suppressions, and the segments campaigns go to."
        primaryAction={canWrite ? <Button variant="primary" onClick={() => setAdding(true)}>Add contact</Button> : undefined}
      />
      <SendingOff />
      <FilterToolbar
        label="Contact filters"
        search={<SearchInput label="Search contacts" value={search} onChange={setSearch} />}
        items={[]}
        anyActive={q !== ""}
        showReset
        onReset={() => setSearch("")}
        meta={contacts.data ? `${contacts.data.counts.reachable.toLocaleString("en-US")} of ${contacts.data.counts.total.toLocaleString("en-US")} reachable by email` : null}
      />
      <DataTable
        caption="Contacts"
        columns={columns}
        {...people}
        rowKey={(c) => c.id}
        loading={contacts.isPending || contacts.isFetching}
        error={contacts.isError ? { title: "Couldn't load contacts", message: message(contacts.error), onRetry: () => void contacts.refetch() } : null}
        empty={q ? "No contacts match." : "No contacts yet. Add people who asked to hear from FlexFreight."}
      />
      <Section
        title="Segments"
        description="Groups of contacts by tag. A campaign goes to one segment; only reachable contacts are emailed."
        flush
      >
        <DataTable
          caption="Segments"
          columns={segColumns}
          {...segments}
          rowKey={(s) => s.id}
          density="compact"
          maxHeight="none"
          loading={segs.isPending}
          error={segs.isError ? { title: "Couldn't load segments", message: message(segs.error), onRetry: () => void segs.refetch() } : null}
          empty="No segments yet."
        />
        {canWrite ? (
          <form
            className="ffa-inlineform ffa-section-foot"
            onSubmit={(e) => {
              e.preventDefault();
              if (segName.trim().length >= 2) addSeg.mutate();
            }}
          >
            <TextField label="Segment name" value={segName} onChange={(e) => setSegName(e.target.value)} maxLength={80} />
            <TextField label="Tags (comma separated)" optional value={segTags} onChange={(e) => setSegTags(e.target.value)} />
            <Button type="submit" size="lg" disabled={segName.trim().length < 2} loading={addSeg.isPending}>Add segment</Button>
          </form>
        ) : null}
      </Section>
      {adding ? <AddContact onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function AddContact({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ email: "", name: "", company: "", tags: "", method: "", evidence: "" });
  const save = useMutation({
    mutationFn: () =>
      api("/v1/console/marketing/contacts", {
        method: "POST",
        body: {
          email: f.email.trim(),
          name: f.name.trim() || null,
          company: f.company.trim() || null,
          tags: f.tags.split(/[\s,]+/).filter(Boolean),
          ...(f.method ? { consent: { channel: "email", method: f.method, evidence: f.evidence.trim() } } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "mkt"] });
      notify.success("Contact added", f.method ? undefined : "No consent recorded: they won't be emailed.");
      onClose();
    },
    onError: (e) => notify.error("Couldn't add the contact", message(e)),
  });
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const valid = emailOk && (!f.method || f.evidence.trim().length >= 3);
  return (
    <Modal
      open
      onClose={onClose}
      title="Add a contact"
      description="Only people who asked to hear from FlexFreight. Say how they agreed, or they stay unreachable."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>Add contact</Button>
        </>
      }
    >
      <div className="ffx-fields">
        <TextField className="ffx-span" label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <TextField label="Name" optional value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <TextField label="Company" optional value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
        <TextField className="ffx-span" label="Tags" optional value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} hint="Comma separated, e.g. shipper, northeast" />
        <Select className="ffx-span" label="How they agreed to email" value={f.method} onChange={(v) => setF({ ...f, method: v })} options={[{ value: "", label: "Not recorded (won't be emailed)" }, { value: "form", label: "Website or portal form" }, { value: "written", label: "In writing" }, { value: "verbal", label: "On a call" }, { value: "existing_customer", label: "Existing customer" }, { value: "double_opt_in", label: "Confirmed by email" }]} />
        {f.method ? <TextField className="ffx-span" label="Proof" value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} hint="When and where, e.g. Rate request form, Oct 18" /> : null}
      </div>
    </Modal>
  );
}

/** Sender name and the postal address every marketing email must carry (CAN-SPAM). */
function SenderSettings() {
  const q = useQuery({ queryKey: ["console", "mkt", "settings"], queryFn: () => api<{ senderName: string | null; postalAddress: string | null; outboundEnabled: boolean; autopilotEnabled: boolean }>("/v1/console/marketing/settings") });
  const [name, setName] = useState("");
  const [addr, setAddr] = useState("");
  useEffect(() => {
    if (q.data) {
      setName(q.data.senderName ?? "");
      setAddr(q.data.postalAddress ?? "");
    }
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api("/v1/console/marketing/settings", { method: "PATCH", body: { senderName: name.trim() || null, postalAddress: addr.trim() || null } }),
    onSuccess: () => notify.success("Sender details saved"),
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  return (
    <Section title="Sender details" description={`Autopilot: ${q.data?.autopilotEnabled ? "on" : "off"}. Sending: ${q.data?.outboundEnabled ? "on" : "off"}. Both stay off until a sending service is connected and Leo approves.`}>
      <form
        className="ffa-inlineform"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <TextField label="Sender name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <TextField label="Mailing address (shown in every footer)" value={addr} onChange={(e) => setAddr(e.target.value)} maxLength={300} />
        <Button type="submit" size="lg" loading={save.isPending}>Save</Button>
      </form>
    </Section>
  );
}
